/**
 * NotificationUnsubscribePage — Iter-273
 * Public page that handles the email-footer unsubscribe link:
 *   https://app.ievntur.com/notifications/unsubscribe?token=<base64 user_id>
 *
 * - Posts the token to the backend, which marks the user opted-out of
 *   non-transactional emails.
 * - Always shows a clear confirmation, even on error (defensive UX —
 *   users should never feel ignored when they click unsubscribe).
 * - Falls back gracefully if the token is missing.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, AlertTriangle, MailMinus, Loader2 } from 'lucide-react';

const NotificationUnsubscribePage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [state, setState] = useState('loading'); // loading | success | invalid | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('invalid');
      setMessage(
        "We couldn't read the unsubscribe link. It may have been cut off " +
        "by your email client. Please reply to the email and we'll handle " +
        "it manually."
      );
      return;
    }
    (async () => {
      try {
        const r = await api.post('/notifications/unsubscribe', { token });
        setState('success');
        setMessage(r?.data?.message || "You've been unsubscribed from marketing emails.");
      } catch (e) {
        setState('error');
        setMessage(
          e?.response?.data?.detail ||
          "We couldn't process this request. Please contact support@ievntur.com and we'll handle it manually."
        );
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card className="max-w-md w-full" data-testid="unsubscribe-card">
        <CardContent className="p-8 text-center space-y-4">
          {state === 'loading' && (
            <>
              <Loader2 className="w-10 h-10 text-cyan-600 animate-spin mx-auto" />
              <p className="text-slate-600">Processing your request…</p>
            </>
          )}
          {state === 'success' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
              <h1 className="text-xl font-semibold text-slate-900">You're unsubscribed</h1>
              <p className="text-slate-600 text-sm">{message}</p>
              <p className="text-slate-500 text-xs">
                Transactional emails (login, payments, bookings) will still be sent
                because they're required for your account.
              </p>
              <Link to="/login">
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="unsubscribe-login-btn">
                  Sign in to manage all preferences
                </Button>
              </Link>
            </>
          )}
          {state === 'invalid' && (
            <>
              <MailMinus className="w-12 h-12 text-amber-600 mx-auto" />
              <h1 className="text-xl font-semibold text-slate-900">Link issue</h1>
              <p className="text-slate-600 text-sm">{message}</p>
            </>
          )}
          {state === 'error' && (
            <>
              <AlertTriangle className="w-12 h-12 text-rose-600 mx-auto" />
              <h1 className="text-xl font-semibold text-slate-900">Hmm, something went wrong</h1>
              <p className="text-slate-600 text-sm">{message}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationUnsubscribePage;
